import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState } from "react";
import Select from "react-select";
import { API_URL } from "..";

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AddStaffLayer = () => {
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [selectedValue, setSelectedValue] = useState(null);
  const [createLogin, setCreateLogin] = useState(true);
  const [message, setMessage] = useState({
    status: false,
    reason: "",
    desc: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Payroll states
  const [payrollType, setPayrollType] = useState("");
  const [rate, setRate] = useState("");

  const [schedules, setSchedules] = useState(
    weekDays.map((day) => ({
      day,
      isWorkingDay: true,
      start_time: "",
      end_time: "",
      late_margin: "",
      early_margin: "",
      total_hours: "",
    }))
  );

  // Image handler
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleChangeRole = (selected) => {
    setSelectedValue(selected || null);
    // Reset payroll fields when changing roles
    setPayrollType("");
    setRate("");
  };

  const handleScheduleChange = (index, field, value) => {
    const updated = [...schedules];
    updated[index][field] = value;

    if (field === "isWorkingDay") {
      updated[index].isWorkingDay = value;
      if (!value) {
        updated[index].start_time = "";
        updated[index].end_time = "";
        updated[index].late_margin = "";
        updated[index].early_margin = "";
        updated[index].total_hours = "";
      }
    }

    if (field === "start_time" || field === "end_time") {
      const start = updated[index].start_time;
      const end = updated[index].end_time;
      if (start && end) {
        const startDate = new Date(`2024-01-01T${start}`);
        const endDate = new Date(`2024-01-01T${end}`);
        const diffHours = (endDate - startDate) / (1000 * 60 * 60);
        updated[index].total_hours = diffHours > 0 ? diffHours.toFixed(2) : "0";
      }
    }

    setSchedules(updated);
  };

  const onFormSubmission = async (event) => {
    event.preventDefault();
    setMessage({ status: false, reason: "", desc: "" });

    const formData = {
      name: event.target.elements.name.value,
      phone: event.target.elements.phone.value,
      role: selectedValue?.value,
      schedules,
    };

    // ✅ Include payroll info for workforce/electrician/user
    const lowerRole = selectedValue?.value?.toLowerCase();
    if (["electrician", "workforce", "user"].includes(lowerRole)) {
      formData.payroll_type = payrollType;
      formData.rate = rate;
    }

    if (createLogin) {
      formData.email = event.target.elements.email.value;
      formData.password = event.target.elements.password.value;
      formData.confirmPassword = event.target.elements.confirmPassword.value;
    }

    // Validation
    if (!formData.name) {
      setMessage({ status: true, reason: "error", desc: "Name is required" });
      return;
    }
    if (!formData.role) {
      setMessage({ status: true, reason: "error", desc: "Role is required" });
      return;
    }

    // ✅ Payroll validation
    if (["electrician", "workforce", "user"].includes(lowerRole)) {
      if (!payrollType) {
        setMessage({
          status: true,
          reason: "error",
          desc: "Please select payroll type",
        });
        return;
      }
      if (!rate) {
        setMessage({
          status: true,
          reason: "error",
          desc: "Please enter a rate",
        });
        return;
      }
    }

    if (createLogin) {
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        setMessage({
          status: true,
          reason: "error",
          desc: "Valid email is required",
        });
        return;
      }
      if (!formData.password) {
        setMessage({
          status: true,
          reason: "error",
          desc: "Password is required",
        });
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setMessage({
          status: true,
          reason: "error",
          desc: "Passwords do not match",
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/user/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.status === 201) {
        setMessage({
          status: true,
          reason: "success",
          desc: "Staff added successfully",
        });
        event.target.reset();
        setSelectedValue(null);
        setPayrollType("");
        setRate("");
      } else {
        throw new Error(data.message || "Failed to add staff");
      }
    } catch (error) {
      setMessage({
        status: true,
        reason: "error",
        desc: `Failed: ${error.message}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-10 col-xl-9 col-lg-10">
            <div className="border">
              <div className="card-body">
                {/* Alerts */}
                {message.status && (
                  <div
                    className={`alert alert-${
                      message.reason === "error" ? "danger" : "success"
                    } alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                    ></button>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={onFormSubmission}>
                  <div className="mb-20">
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      placeholder="Enter Full Name"
                    />
                  </div>

                  <div className="mb-20">
                    <label className="form-label fw-semibold">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone"
                      name="phone"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="mb-20">
                    <label className="form-label fw-semibold">
                      Select Role <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={[
                        { label: "Admin", value: "admin" },
                        { label: "User", value: "user" },
                        { label: "Electrician", value: "electrician" },
                      ]}
                      placeholder="Select Role"
                      value={selectedValue}
                      onChange={handleChangeRole}
                    />
                  </div>

                  {/* ✅ Payroll Section */}
                  {["electrician", "workforce", "user"].includes(
                    selectedValue?.value?.toLowerCase()
                  ) && (
                    <div className="mb-20 border p-3 rounded bg-light">
                      <label className="form-label fw-semibold mb-2">
                        Payroll Details
                      </label>

                      <div className="d-flex align-items-center mb-3">
                        <div className="form-check me-3">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="payroll_type"
                            id="daily"
                            value="daily"
                            checked={payrollType === "daily"}
                            onChange={(e) => setPayrollType(e.target.value)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="daily"
                          >
                            Daily
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="payroll_type"
                            id="hourly"
                            value="hourly"
                            checked={payrollType === "hourly"}
                            onChange={(e) => setPayrollType(e.target.value)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="hourly"
                          >
                            Hourly
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="form-label fw-semibold">
                          Rate <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter rate (e.g. 120)"
                          value={rate}
                          onChange={(e) => setRate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Email / Password */}
                  {createLogin && (
                    <>
                      <div className="mb-20">
                        <label className="form-label fw-semibold">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          placeholder="Enter email"
                        />
                      </div>

                      <div className="mb-20">
                        <label className="form-label fw-semibold">
                          Password <span className="text-danger">*</span>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          name="password"
                          placeholder="*******"
                        />
                      </div>

                      <div className="mb-20">
                        <label className="form-label fw-semibold">
                          Confirm Password <span className="text-danger">*</span>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="*******"
                        />
                      </div>
                    </>
                  )}

                  {/* Schedule Table (unchanged) */}
                  {/* ... existing schedule code ... */}
                  <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-semibold m-0">Weekly Schedule</h6>
        <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
                if (!schedules[0].start_time || !schedules[0].end_time) return;
                const base = schedules[0];
                const updated = schedules.map((sch, i) =>
                    i === 0
                        ? sch
                        : {
                              ...sch,
                              isWorkingDay: base.isWorkingDay,
                              start_time: base.start_time,
                              end_time: base.end_time,
                              late_margin: base.late_margin,
                              early_margin: base.early_margin,
                              total_hours: base.total_hours
                          }
                );
                setSchedules(updated);
            }}
            disabled={!schedules[0].start_time || !schedules[0].end_time}
        >
            Apply to All Days
        </button>
    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-center">
                        <thead className="table-light">
                          <tr>
                            <th>Day</th>
                            <th>Working?</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Late Margin (min)</th>
                            <th>Early Out (min)</th>
                            <th>Total Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedules.map((sch, index) => (
                            <tr key={index}>
                              <td>{sch.day}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={sch.isWorkingDay}
                                  className="form-check-input"
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      index,
                                      "isWorkingDay",
                                      e.target.checked
                                    )
                                  }
                                />{" "}
                                {sch.isWorkingDay ? "Yes" : "Off"}
                              </td>
                              <td>
                                <input
                                  type="time"
                                  className="form-control"
                                  value={sch.start_time}
                                  disabled={!sch.isWorkingDay}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      index,
                                      "start_time",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="time"
                                  className="form-control"
                                  value={sch.end_time}
                                  disabled={!sch.isWorkingDay}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      index,
                                      "end_time",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={sch.late_margin}
                                  placeholder="0"
                                  disabled={!sch.isWorkingDay}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      index,
                                      "late_margin",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={sch.early_margin}
                                  placeholder="0"
                                  disabled={!sch.isWorkingDay}
                                  onChange={(e) =>
                                    handleScheduleChange(
                                      index,
                                      "early_margin",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                {sch.isWorkingDay
                                  ? sch.total_hours || "-"
                                  : "Holiday/Off"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary px-5"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStaffLayer;
